import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  Inventory2,
  Sell,
  ShoppingCart,
  PriceCheck,
  Warehouse,
  CheckCircleOutline,
  AssignmentReturn,
  TrendingUp,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
} from 'recharts';
import {
  PageHeader,
  StatCard,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  TypePill,
  TableEmptyState,
  ErrorState,
} from '../../components/common/pageKit';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';

// ---------------------------------------------------------------------------
// PRODUCT 360 — a single product's master row plus real, tenant-scoped
// aggregates (sales, returns, stock, open orders). Read-only; mirrors the
// detail-page idiom of SalesOrderDetailPage.tsx and the stat/chart treatment
// of the finance 360 pages. Data comes from GET /product-analytics/:id/360.
// ---------------------------------------------------------------------------

interface ProductMaster {
  id: number;
  code: string;
  name: string;
  hsnCode: string | null;
  gstRate: number | null;
  unitOfMeasure: string | null;
  basePrice: number | null;
  category: string | null;
  materialType: string | null;
  isStockItem: boolean;
  isActive: boolean;
}

interface FyBucket {
  label: string;
  qty: number;
  value: number;
}

interface Product360 {
  product: ProductMaster;
  sales: {
    totalQtySold: number;
    totalValue: number;
    avgSellingPrice: number;
    currentFy: FyBucket;
    previousFy: FyBucket;
    valueBasis: string;
  };
  monthlyTrend: { month: string; qty: number; value: number }[];
  topCustomers: { customerId: number | null; customerName: string; qty: number; value: number }[];
  returns: { qty: number; value: number };
  stock: { onHand: number; committed: number; available: number };
  openOrders: { qty: number; value: number };
  generatedAt: string;
}

const fmtQty = (n: number) => formatIndianNumber(Number(n) || 0);
const monthLabel = (ym: string) => {
  // 'YYYY-MM' -> 'Mon YY'
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
};

const Product360Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const ct = useChartTokens();

  const [data, setData] = useState<Product360 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { product360 } = await api.get<{ product360: Product360 }>(`/product-analytics/${id}/360`);
      setData(product360);
    } catch (err: any) {
      setError(err.message || 'Failed to load product 360');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/products')} sx={{ mb: 2 }}>
          Back to Products
        </Button>
        <TableSurface>
          <ErrorState message={error || 'Product not found'} onRetry={load} />
        </TableSurface>
      </Box>
    );
  }

  const { product, sales, monthlyTrend, topCustomers, returns, stock, openOrders } = data;
  const chartData = monthlyTrend.map((m) => ({ ...m, label: monthLabel(m.month) }));
  const hasSales = sales.totalQtySold > 0 || sales.totalValue > 0;

  const subtitleBits = [
    product.category ? product.category : null,
    product.hsnCode ? `HSN ${product.hsnCode}` : null,
    product.unitOfMeasure ? `Unit ${product.unitOfMeasure}` : null,
    product.gstRate != null ? `GST ${product.gstRate}%` : null,
  ].filter(Boolean);

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/products')} sx={{ mb: 1 }}>
        Back to Products
      </Button>

      <PageHeader
        title={product.name}
        qualifier={`· ${product.code}`}
        subtitle={subtitleBits.join('  ·  ')}
        actions={
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
            {product.materialType && <TypePill label={product.materialType} tint={c.purple} />}
            {!product.isStockItem && <TypePill label="NON-STOCK" tint={c.sub} />}
            <StatusDot
              label={product.isActive ? 'Active' : 'Inactive'}
              tone={product.isActive ? 'success' : 'neutral'}
            />
          </Box>
        }
      />

      {/* KPI row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
          gap: 2,
          mb: 2.5,
        }}
      >
        <StatCard title="Total Sales Value" value={formatCurrency(sales.totalValue)} icon={<Sell />} color="#2563eb" />
        <StatCard title="Qty Sold" value={fmtQty(sales.totalQtySold)} icon={<ShoppingCart />} color="#0891b2" />
        <StatCard title="Avg Selling Price" value={formatCurrency(sales.avgSellingPrice)} icon={<PriceCheck />} color="#7c3aed" />
        <StatCard
          title="On-hand"
          value={product.isStockItem ? fmtQty(stock.onHand) : null}
          icon={<Warehouse />}
          color="#059669"
        />
        <StatCard
          title="Available"
          value={product.isStockItem ? fmtQty(stock.available) : null}
          icon={<CheckCircleOutline />}
          color="#16a34a"
        />
        <StatCard title="Returns" value={formatCurrency(returns.value)} icon={<AssignmentReturn />} color="#dc2626" />
      </Box>

      <Grid container spacing={2.5}>
        {/* Monthly sales trend */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <TrendingUp fontSize="small" /> Monthly Sales Trend
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Last 12 months · value (bars) and quantity (line), net of GST
            </Typography>
            {hasSales ? (
              <Box sx={{ height: 280, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 6, right: 8, bottom: 0, left: -8 }}>
                    <CartesianGrid {...gridProps(ct)} />
                    <XAxis dataKey="label" {...axisProps(ct)} />
                    <YAxis yAxisId="value" {...axisProps(ct)} width={62} tickFormatter={(v) => formatIndianNumber(v)} />
                    <YAxis yAxisId="qty" orientation="right" {...axisProps(ct)} width={44} tickFormatter={(v) => formatIndianNumber(v)} />
                    <RTooltip
                      {...tooltipStyle(ct)}
                      formatter={(v: any, n: any) => [n === 'Quantity' ? fmtQty(Number(v)) : formatCurrency(Number(v)), n]}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="value" dataKey="value" name="Sales Value" fill={ct.series(0)} radius={[3, 3, 0, 0]} maxBarSize={30} />
                    <Line yAxisId="qty" type="monotone" dataKey="qty" name="Quantity" stroke={ct.series(1)} strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <TableEmptyState title="No sales in the last 12 months" hint="This product has not been invoiced yet." />
            )}
          </Paper>
        </Grid>

        {/* Financial-year split + stock panel */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Sales by Financial Year
            </Typography>
            {[sales.currentFy, sales.previousFy].map((fy, idx) => (
              <Box key={fy.label} sx={{ mb: idx === 0 ? 1.5 : 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {fy.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtQty(fy.qty)} {product.unitOfMeasure || 'units'}
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: idx === 0 ? c.accent : 'text.primary' }}>
                  {formatCurrency(fy.value)}
                </Typography>
              </Box>
            ))}
          </Paper>

          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Inventory2 fontSize="small" /> Stock &amp; Availability
            </Typography>
            {product.isStockItem ? (
              <>
                {[
                  ['On-hand (all warehouses)', stock.onHand, 'text.primary'],
                  ['Committed (active reservations)', stock.committed, 'warning.main'],
                ].map(([label, val]) => (
                  <Box key={label as string} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtQty(val as number)}
                    </Typography>
                  </Box>
                ))}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Available</Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: stock.available > 0 ? 'success.main' : 'error.main' }}
                  >
                    {fmtQty(stock.available)}
                  </Typography>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                This is a non-stock product — no inventory is tracked for it.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Top customers */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <TableSurface>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Top Customers
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Top 5 by lifetime sales value for this product
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Customer</TableCell>
                    <TableCell sx={headerCellSx} align="right">Qty</TableCell>
                    <TableCell sx={headerCellSx} align="right">Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topCustomers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ border: 0 }}>
                        <TableEmptyState title="No customer sales yet" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    topCustomers.map((cust, i) => (
                      <TableRow
                        key={`${cust.customerId ?? 'x'}-${i}`}
                        sx={{ ...bodyRowSx, cursor: cust.customerId ? 'pointer' : 'default' }}
                        hover
                        onClick={() => cust.customerId && navigate(`/sales/customers/${cust.customerId}/edit`)}
                      >
                        <TableCell sx={{ py: 1.25, fontWeight: 600 }}>{cust.customerName}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtQty(cust.qty)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                          {formatCurrency(cust.value)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Grid>

        {/* Open orders + returns panel */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <ShoppingCart fontSize="small" /> Open Orders
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Open quantity</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtQty(openOrders.qty)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Open value</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(openOrders.value)}</Typography>
            </Box>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
              Sales orders not yet invoiced or cancelled.
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <AssignmentReturn fontSize="small" /> Returns
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Returned quantity</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtQty(returns.qty)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Returned value</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'error.main' }}>
                {formatCurrency(returns.value)}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
              Excludes cancelled returns.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Product360Page;
